import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../components/AppIcon';
import Button from '../components/ui/Button';

const LandingPage = () => {
  const navigate = useNavigate();

  const stats = [
    { label: 'Total Value Locked', value: '$2.4M', change: '+12.5%' },
    { label: 'Active Stakers', value: '1,247', change: '+8.2%' },
    { label: 'Rewards Distributed', value: '45,678', change: '+25.1%' },
    { label: 'Assets Supported', value: '50+', change: '+100%' }
  ];

  const features = [
    {
      icon: 'Wallet',
      title: 'Multi-Network Support',
      description: 'Connect to Ethereum Sepolia and Polygon Amoy testnets seamlessly',
      color: 'primary'
    },
    {
      icon: 'Lock',
      title: 'Gasless Staking',
      description: 'Stake your assets without gas fees using MetaMask DTK and ERC-4337',
      color: 'secondary'
    },
    {
      icon: 'ArrowUpDown',
      title: 'Cross-Chain Swaps',
      description: 'Convert rewards across chains using LI.FI SDK and CCTP V2',
      color: 'accent'
    },
    {
      icon: 'Coins',
      title: 'Real-World Value',
      description: 'Deposit USDC to MetaMask wallet for MetaMask Card spending',
      color: 'warning'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-surface/80 backdrop-blur-sm border-b border-border">
        <div className="px-4 lg:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-lg">
                <Icon name="Zap" size={24} color="white" />
              </div>
              <h1 className="text-xl font-bold text-text-primary">Cross-Chain Rewards Hub</h1>
            </div>
            
            <div className="flex items-center space-x-4">
              <Button
                variant="primary"
                onClick={() => navigate('/asset-dashboard-portfolio-overview')}
                iconName="LayoutGrid"
                iconPosition="left"
              >
                Launch App
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-background/90 to-background/70 z-10"></div>
          <img 
            src="https://images.unsplash.com/photo-1639322537228-f710d846310a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxibG9ja2NoYWlufGVufDB8fHx8MTc1MjIzOTkyM3ww&ixlib=rb-4.1.0&q=85"
            alt="Blockchain Network"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="relative z-20 max-w-7xl mx-auto px-4 lg:px-6 text-center">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 mb-6">
              <Icon name="Zap" size={16} className="text-primary" />
              <span className="text-sm font-medium text-primary">Cross-Chain DeFi Platform</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold text-text-primary mb-6">
              Unlock Value from Your
              <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                {" "}Idle Assets
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-text-secondary mb-8 max-w-3xl mx-auto">
              Stake your NFTs and tokens across Ethereum Sepolia and Polygon Amoy testnets. 
              Earn rewards, convert to USDC, and unlock real-world spending power.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                variant="primary"
                size="lg"
                onClick={() => navigate('/asset-dashboard-portfolio-overview')}
                iconName="LayoutGrid"
                iconPosition="left"
                className="sm:w-auto w-full"
              >
                Launch App
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                onClick={() => navigate('/wallet-connection-network-setup')}
                iconName="Wallet"
                iconPosition="left"
                className="sm:w-auto w-full"
              >
                Connect Wallet
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-surface/50">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl font-bold text-text-primary mb-2">{stat.value}</div>
                <div className="text-sm text-text-secondary mb-1">{stat.label}</div>
                <div className="text-xs text-success font-medium">{stat.change}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-text-primary mb-4">
              Why Choose Cross-Chain Rewards Hub?
            </h2>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto">
              Built with MetaMask SDK, Circle Wallets, and LI.FI Protocol for seamless Web3 experience
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-surface border border-border rounded-lg p-6 hover:border-border-light transition-smooth">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name={feature.icon} size={24} className="text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-text-primary mb-2">{feature.title}</h3>
                <p className="text-text-secondary">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-surface border-t border-border">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-lg">
                <Icon name="Zap" size={20} color="white" />
              </div>
              <span className="text-text-primary font-semibold">Cross-Chain Rewards Hub</span>
            </div>
            
            <div className="text-text-muted text-sm">
              © 2024 Cross-Chain Rewards Hub. Built for MetaMask Card Dev Cook-Off.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;